import { useState } from "react"
import { Trash2 } from "lucide-react"

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"

/**
 * document-editor.md's DE-delete — a real shared component (also used by
 * `details.md`'s view-mode header for the identical trigger), not
 * page-local markup. Its `AlertDialogContent` has no reason `Textarea`,
 * unlike the reason-required `DeleteDraftDialog` used by the documents
 * list's row actions — a different component this one is not a variant of.
 * Uses a plain `Button` + manual `open` state rather than `AlertDialogAction`
 * (which always dismisses on click) so a `409 DOCUMENT_UPDATE_CONFLICT` from
 * `deleteDraft` — a real possibility now it calls the backend — surfaces
 * instead of the dialog silently closing as if the delete had succeeded.
 */
export function HeaderDeleteDocumentDialog({
  documentName,
  onDelete,
}: {
  documentName: string
  onDelete: () => void | Promise<void>
}) {
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleConfirm() {
    if (submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await onDelete()
      setOpen(false)
    } catch {
      setError("Не вдалося видалити документ. Спробуйте ще раз.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AlertDialog
      onOpenChange={(nextOpen) => {
        if (nextOpen) setError(null)
        setOpen(nextOpen)
      }}
      open={open}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <AlertDialogTrigger asChild>
            <Button
              aria-label="Видалити Документ"
              className="rounded-full text-destructive hover:bg-destructive/10 hover:text-destructive"
              size="icon-lg"
              variant="destructive"
            >
              <Trash2 />
            </Button>
          </AlertDialogTrigger>
        </TooltipTrigger>
        <TooltipContent>Видалити цей документ</TooltipContent>
      </Tooltip>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Видалити документ</AlertDialogTitle>
          <AlertDialogDescription>
            Документ {documentName} буде видалено
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>Відмінити</AlertDialogCancel>
          <Button disabled={submitting} onClick={handleConfirm} variant="destructive">
            <Trash2 data-icon="inline-start" />
            {submitting ? "Видалення…" : "Видалити Документ"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
